/**
 * TURNING THE NUMBER INTO WORDS
 * =============================
 * The hero readout is a power of ten (the graspable unit), but a bare exponent
 * still needs a human gloss. These helpers turn the running total into:
 *   - `plain`: plain-language odds ("about a 1 in 77 million chance that even
 *              one other exists, anywhere")
 *   - `analogy`: a one-line cosmic analogy keyed to the magnitude.
 *
 * For a count `N` below one we read it the Poisson way: the probability that at
 * least one civilisation exists is 1 - e^(-N) ≈ N for small N, so "expected
 * count" and "chance one exists" coincide and we can speak in odds.
 */

export interface ResultDescription {
  plain: string;
  analogy: string;
}

/** "13 million", "1.2 billion", "3.4 trillion", or scientific beyond. */
export function plainCount(n: number): string {
  if (!isFinite(n)) return "∞";
  const units = [
    { v: 1e12, s: "trillion" },
    { v: 1e9, s: "billion" },
    { v: 1e6, s: "million" },
    { v: 1e3, s: "thousand" },
  ];
  for (const u of units) {
    if (n >= u.v && n < u.v * 1000) {
      const val = n / u.v;
      const r = val >= 100 ? Math.round(val) : Math.round(val * 10) / 10;
      return `${r} ${u.s}`;
    }
  }
  if (n >= 1e15) {
    const exp = Math.floor(Math.log10(n));
    return `10^${exp}`;
  }
  return String(Math.round(n));
}

/** A cosmic analogy chosen by order of magnitude of the expected count. */
function analogyFor(total: number): string {
  if (total <= 0) {
    return "Alone beyond reckoning — you could run the universe again and again and still find no one.";
  }
  const log = Math.log10(total);
  if (log >= 6) {
    return "A crowded cosmos — millions of voices should be filling the sky.";
  }
  if (log >= 3) {
    return "Thousands of neighbours, scattered across the galaxies.";
  }
  if (log >= 0) {
    return "A small society of worlds — we would have company.";
  }
  if (log >= -3) {
    return "Perhaps a handful. Perhaps only us.";
  }
  if (log >= -12) {
    return "On these numbers, we are alone in the observable universe.";
  }
  return "Alone many times over — the universe would need to be reborn beyond counting to expect company.";
}

export function describeResult(total: number): ResultDescription {
  if (!isFinite(total) || total <= 0) {
    return {
      plain: "vanishingly close to zero",
      analogy: analogyFor(0),
    };
  }

  if (total >= 1) {
    const plain =
      total >= 1000
        ? `about ${plainCount(total)} expected, universe-wide`
        : `about ${Math.round(total).toLocaleString("en-US")} expected, universe-wide`;
    return { plain, analogy: analogyFor(total) };
  }

  // total < 1 — speak in odds of even one existing.
  const inv = 1 / total;
  return {
    plain: `about a 1 in ${plainCount(inv)} chance that even one other exists, anywhere`,
    analogy: analogyFor(total),
  };
}

/** Split a positive number into mantissa + base-10 exponent for display. */
export function splitScientific(value: number): {
  mantissa: number;
  exponent: number;
} {
  if (!isFinite(value) || value <= 0) return { mantissa: 0, exponent: 0 };
  const exponent = Math.floor(Math.log10(value));
  const mantissa = value / Math.pow(10, exponent);
  return { mantissa, exponent };
}
